import Analytics, { UNLOAD_TRACK_OPTIONS } from './Analytics';
import { LicenseErrorCode } from '../tenant/providerBilling';

/**
 * The licence paywall events of the WLP Mixpanel V1 tracking plan (PRD Part A §6.4,
 * backend `AnalyticsEvent::PaywallViewed` / `PaywallOutcome`). Client-side only.
 *
 *   paywall_viewed  { gate }
 *   paywall_outcome { gate, outcome }   — exactly one per view, the page unloading included
 *
 * subscription_changed (the licence actually changing) is the backend's.
 */
export const PaywallEvent = {
  Viewed: 'paywall_viewed',
  Outcome: 'paywall_outcome',
};

/**
 * The value moment a paywall stands in front of; also sent as `gate` with the
 * licence activation so the backend's subscription_changed names it.
 * `publish_website` has no paywall in the dashboard yet.
 */
export const PaywallGate = {
  LiveOrders: 'live_orders',
  Reservations: 'reservations',
  GoogleAiReviews: 'google_ai_reviews',
  MobileApp: 'mobile_app',
  PublishWebsite: 'publish_website',
};

export const PaywallOutcome = {
  Subscribed: 'subscribed',
  BlockedNoPaymentMethod: 'blocked_no_payment_method',
  BlockedNoBase: 'blocked_no_base',
  Dismissed: 'dismissed',
};

/** The launch checks that name a paywall blocker. */
const BLOCKING_CHECKS = {
  billing_payment_method: PaywallOutcome.BlockedNoPaymentMethod,
  billing_license: PaywallOutcome.BlockedNoBase,
};

/**
 * What stood in the way, from a refusal: a missing payment method or a missing
 * base licence — or null for any other refusal. With both, the payment method
 * wins: without one nothing can be bought, the base licence included.
 */
export function paywallBlocker({ error = null, failedChecks = [] } = {}) {
  if (error?.error_code === LicenseErrorCode.PaymentMethodRequired) {
    return PaywallOutcome.BlockedNoPaymentMethod;
  }

  if (error?.error_code === LicenseErrorCode.LicenseRequired) {
    return PaywallOutcome.BlockedNoBase;
  }

  const blockers = (failedChecks ?? []).map((check) => BLOCKING_CHECKS[check]).filter(Boolean);

  if (blockers.includes(PaywallOutcome.BlockedNoPaymentMethod)) {
    return PaywallOutcome.BlockedNoPaymentMethod;
  }

  return blockers[0] ?? null;
}

/**
 * One paywall view, from the moment it is shown to the one outcome it ends with.
 *
 * `show()` sends paywall_viewed (once). `block(outcome)` remembers what stood in the
 * way — the payment-method step appearing, an activation refused for want of a base
 * licence — so a view that then closes without a purchase ends blocked rather than
 * dismissed. `settle(outcome)` sends paywall_outcome, once, and only for a view that
 * was shown. `end()` settles with the blocker, else `dismissed`.
 *
 * A page that goes away with the view still open — the tab closed, a reload, a hard
 * link followed — runs no React cleanup, so the view ends on `pagehide` instead, as
 * `end()` would, with the event sent by beacon. `settle(outcome, { unloading: true })`
 * is for a view the screen ends itself right before a navigation (leaving for Stripe).
 * A settled view stays settled: a page restored from the back-forward cache with the
 * paywall still open needs a new view, which its owner starts on `pageshow`.
 */
export function createPaywallView(gate, analytics = Analytics, target = globalThis.window) {
  let shown = false;
  let settled = false;
  let blocker = null;

  const settle = (outcome, { unloading = false } = {}) => {
    if (!shown || settled || !outcome) {
      return;
    }

    settled = true;
    target?.removeEventListener('pagehide', endOnUnload);

    if (unloading) {
      analytics.track(PaywallEvent.Outcome, { gate, outcome }, UNLOAD_TRACK_OPTIONS);
    } else {
      analytics.track(PaywallEvent.Outcome, { gate, outcome });
    }
  };

  function endOnUnload() {
    settle(blocker ?? PaywallOutcome.Dismissed, { unloading: true });
  }

  return {
    gate,
    get shown() {
      return shown;
    },
    get settled() {
      return settled;
    },
    show: () => {
      if (shown || settled || !gate) {
        return;
      }

      shown = true;
      target?.addEventListener('pagehide', endOnUnload);
      analytics.track(PaywallEvent.Viewed, { gate });
    },
    block: (outcome) => {
      if (outcome) {
        blocker = outcome;
      }
    },
    settle,
    end: () => settle(blocker ?? PaywallOutcome.Dismissed),
  };
}
